import React, { useState, useEffect } from 'react';
import PointStyleModal from '../modals/PointStyleModal';
import PolygonStyleModal from '../modals/PolygonStyleModal';
import LineStyleModal from '../modals/LineStyleModal';
import ImportWizardModal from '../../import-wizard/ImportWizardModal';
import ImportOSMLocationsModal from '../modals/ImportOSMLocationsModal';
import IsochroneModal from '../modals/IsochroneModal';
import GenerateMeetingPointsModal from '../modals/GenerateMeetingPointsModal';
import { FeatureGate } from '../../common/FeatureGate';
import TransitScanModal from '../modals/TransitScanModal';
import ErrorMessageBox from './ErrorMessageBox';
import FleetVehicleModal from './FleetVehicleModal';
import SimulationModal from '../modals/SimulationModal';
import GenerateRidesDrawer from './GenerateRidesDrawer';
import FixedRoutesDrawer from './FixedRoutesDrawer';
import AttributeTableDrawer from '../../layer_panel/AttributeTableDrawer';
import type { LayerTarget } from '../../import-wizard/types';
import { Point, Polygon, Line, Layer, TripImportSquishMeta } from '../../../context/context';
import { Vehicle } from '../../../types/vehicle';
import { GlobalContext } from '../../../context/context';
import { Fleet } from './FleetMenu';
import { MutableRefObject } from 'react';

interface MapModalsManagerProps {
  isPointModalOpen: boolean;
  setIsPointModalOpen: (open: boolean) => void;
  selectedPoint: Point | null;
  setSelectedPoint: (point: Point | null) => void;
  global: GlobalContext;
  editLayer: (updatedLayer: any, layerId: number) => void;
  isPolygonModalOpen: boolean;
  setIsPolygonModalOpen: (open: boolean) => void;
  selectedPolygon: Polygon | null;
  setSelectedPolygon: (polygon: Polygon | null) => void;
  isLineModalOpen: boolean;
  setIsLineModalOpen: (open: boolean) => void;
  selectedLine: Line | null;
  setSelectedLine: (line: Line | null) => void;
  isImportTripsModalOpen: boolean;
  setIsImportTripsModalOpen: (open: boolean) => void;
  droppedFile: any;
  setDroppedFile: (file: any) => void;
  handleImportTrips: (points: Point[], lines: Line[], opts: any) => void;
  isImportGTFSModalOpen: boolean;
  setIsImportGTFSModalOpen: (open: boolean) => void;
  gtfsError: string | null;
  setGtfsError: (err: string | null) => void;
  isImportKMLModalOpen: boolean;
  setIsImportKMLModalOpen: (open: boolean) => void;
  handleImportKMLShapes: (shapes: any, opts: any) => void;
  osmImportProgress: number | null;
  isOsmLocationsModalOpen: boolean;
  setIsOsmLocationsModalOpen: (open: boolean) => void;
  handleOsmLocationsImport: (opts: any) => void;
  osmLocationsLoading: boolean;
  osmLocationsPolygon: Polygon | null;
  isIsochroneModalOpen: boolean;
  setIsIsochroneModalOpen: (open: boolean) => void;
  isochronePoint: Point | null;
  setIsochronePoint: (point: Point | null) => void;
  setIsochroneError: (err: string | null) => void;
  isochroneLoading: boolean;
  isochroneError: string | null;
  isMeetingPointsModalOpen: boolean;
  setIsMeetingPointsModalOpen: (open: boolean) => void;
  meetingPointsPolygon: Polygon | null;
  setMeetingPointsPolygon: (polygon: Polygon | null) => void;
  meetingPointsLoading: boolean;
  setMeetingPointsLoading: (loading: boolean) => void;
  onMeetingPointsLayerCreated?: (layerId: number, layerName: string) => void;
  transitScanState: any;
  transitScanActions: any;
  vehicleModalOpen: boolean;
  setVehicleModalOpen: (open: boolean) => void;
  editingVehicle: Vehicle | null;
  setEditingVehicle: (v: Vehicle | null) => void;
  vehicleDraft: Vehicle | null;
  setVehicleDraft: (v: Vehicle | null) => void;
  handleAddOrEditVehicle: (v: Vehicle) => void;
  allPoints: { id: string; name: string }[];
  isSimulationModalOpen: boolean;
  setIsSimulationModalOpen: (open: boolean) => void;
  fleets: Fleet[];
  layersForModal: { id: string; name: string; type: 'point' | 'line' }[];
  isGenerateRidesDrawerOpen: boolean;
  setIsGenerateRidesDrawerOpen: (open: boolean) => void;
  onGenerateRides: (config: any) => void;
  isFixedRoutesDrawerOpen: boolean;
  setIsFixedRoutesDrawerOpen: (open: boolean) => void;
  selectedRouteId?: string | null;
  isAttributeTableDrawerOpen: boolean;
  setIsAttributeTableDrawerOpen: (open: boolean) => void;
  mapLayers: Layer[];
  attributeTableLayerId: number;
  setAttributeTableLayerId: (id: number) => void;
  onAttributeTableShapeUpdate?: (layerId: number, shapeId: string, patch: Record<string, any>) => boolean | void;
  onAttributeTableUndo?: () => void;
  onAttributeTableRedo?: () => void;
  attributeTableCanUndo?: boolean;
  attributeTableCanRedo?: boolean;
  attributeTableEditable?: boolean;
  importModalOpen: boolean;
  setImportModalOpen: (open: boolean) => void;
  importedFile: File | null;
  setImportedFile: (file: File | null) => void;
  handleImportedShapes: (
    global: GlobalContext,
    shapes: any,
    meta?: { tripImportSquish?: TripImportSquishMeta }
  ) => void;
  mapRef: MutableRefObject<L.Map | null>;
  setIsochroneLoading: (loading: boolean) => void;
  onRunSimulation: (config: any) => void;
  setSimulationProgress?: (progress: any) => void;
  onOpenSimulationDrawer?: () => void;
  onEditFleet?: (fleetId: string) => void;
}

const MapModalsManager: React.FC<MapModalsManagerProps> = (props) => {
  // Destructure all props as needed
  const {
    isPointModalOpen, setIsPointModalOpen, selectedPoint, setSelectedPoint, global, editLayer,
    isPolygonModalOpen, setIsPolygonModalOpen, selectedPolygon, setSelectedPolygon,
    isLineModalOpen, setIsLineModalOpen, selectedLine, setSelectedLine,
    isImportTripsModalOpen, setIsImportTripsModalOpen, droppedFile, setDroppedFile,
    isImportGTFSModalOpen, setIsImportGTFSModalOpen, gtfsError, setGtfsError,
    isImportKMLModalOpen, setIsImportKMLModalOpen,
    osmImportProgress, isOsmLocationsModalOpen, setIsOsmLocationsModalOpen, handleOsmLocationsImport, osmLocationsLoading, osmLocationsPolygon,
    isIsochroneModalOpen, setIsIsochroneModalOpen, isochronePoint, setIsochronePoint, setIsochroneError, isochroneLoading, isochroneError,
    isMeetingPointsModalOpen, setIsMeetingPointsModalOpen, meetingPointsPolygon, setMeetingPointsPolygon, meetingPointsLoading, setMeetingPointsLoading, onMeetingPointsLayerCreated,
    transitScanState, transitScanActions,
    vehicleModalOpen, setVehicleModalOpen, editingVehicle, setEditingVehicle, vehicleDraft, setVehicleDraft, handleAddOrEditVehicle, allPoints,
    isSimulationModalOpen, setIsSimulationModalOpen, fleets, layersForModal,
    isGenerateRidesDrawerOpen, setIsGenerateRidesDrawerOpen, onGenerateRides,
    isFixedRoutesDrawerOpen, setIsFixedRoutesDrawerOpen, selectedRouteId,
    isAttributeTableDrawerOpen, setIsAttributeTableDrawerOpen, mapLayers, attributeTableLayerId, setAttributeTableLayerId,
    onAttributeTableShapeUpdate, onAttributeTableUndo, onAttributeTableRedo, attributeTableCanUndo, attributeTableCanRedo, attributeTableEditable,
    importModalOpen, setImportModalOpen, importedFile, setImportedFile, handleImportedShapes,
    mapRef, setIsochroneLoading, onRunSimulation,
    setSimulationProgress,
    onOpenSimulationDrawer,
    onEditFleet,
  } = props;

  const [initialSelectedLayer, setInitialSelectedLayer] = useState<string | undefined>(undefined);
  const [initialSelectedFleetId, setInitialSelectedFleetId] = useState<string | undefined>(undefined);

  return <>
    <PointStyleModal
      open={isPointModalOpen}
      onClose={() => setIsPointModalOpen(false)}
      point={selectedPoint}
      global={global}
      editLayer={editLayer}
      setSelectedPoint={setSelectedPoint}
      setIsPointModalOpen={setIsPointModalOpen}
    />
    <PolygonStyleModal
      open={isPolygonModalOpen}
      onClose={() => setIsPolygonModalOpen(false)}
      polygon={selectedPolygon}
      global={global}
      editLayer={editLayer}
      setSelectedPolygon={setSelectedPolygon}
      setIsPolygonModalOpen={setIsPolygonModalOpen}
    />
    <LineStyleModal
      open={isLineModalOpen}
      onClose={() => setIsLineModalOpen(false)}
      line={selectedLine}
      global={global}
      editLayer={editLayer}
      setSelectedLine={setSelectedLine}
      setIsLineModalOpen={setIsLineModalOpen}
    />
    <ImportWizardModal
      open={isImportTripsModalOpen || isImportGTFSModalOpen || isImportKMLModalOpen || importModalOpen}
      onClose={() => {
        setIsImportTripsModalOpen(false);
        setIsImportGTFSModalOpen(false);
        setIsImportKMLModalOpen(false);
        setImportModalOpen(false);
        setDroppedFile(null);
        setImportedFile(null);
      }}
      onImport={(shapes, meta, layerTarget) => {
        // handleImportedShapes creates the new layer, zooms to it, and logs
        // analytics for every shape kind (points, lines, polygons), so it is the
        // single source of truth for wizard imports. The old handleImportTrips /
        // handleImportKMLShapes calls here were both redundant (they created a
        // duplicate layer) and broken (they were passed `undefined` for their
        // `{ global, editLayer, setShouldFitBounds }` props, throwing
        // "Cannot destructure property 'global' of 'undefined'").
        handleImportedShapes(global, shapes, meta);
      }}
      existingLayers={mapLayers}
      droppedFile={droppedFile?.file || importedFile}
    />
    {gtfsError && <ErrorMessageBox message={gtfsError} />}
    {osmImportProgress !== null && (
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 3000 }}>
        {/* You can add a LinearProgress here if needed */}
      </div>
    )}
    <FeatureGate featureName="import_osm_locations">
      <ImportOSMLocationsModal
        open={isOsmLocationsModalOpen}
        onClose={() => setIsOsmLocationsModalOpen(false)}
        onImport={(selectedTypes, polygon) => handleOsmLocationsImport({ selectedTypes, polygon })}
        loading={osmLocationsLoading}
        polygon={osmLocationsPolygon}
      />
    </FeatureGate>
    <FeatureGate featureName="openroute_routing">
      <IsochroneModal
        open={isIsochroneModalOpen}
        onClose={() => {
          setIsIsochroneModalOpen(false);
          setIsochronePoint(null);
          setIsochroneError(null);
        }}
        point={isochronePoint}
        global={global}
        mapRef={mapRef}
        setIsochroneLoading={setIsochroneLoading}
        setIsochroneError={setIsochroneError}
        setIsIsochroneModalOpen={setIsIsochroneModalOpen}
        setIsochronePoint={setIsochronePoint}
        loading={isochroneLoading}
        error={isochroneError}
      />
    </FeatureGate>
    <FeatureGate featureName="meeting_points_generator">
      <GenerateMeetingPointsModal
        open={isMeetingPointsModalOpen}
        onClose={() => {
          setIsMeetingPointsModalOpen(false);
          setMeetingPointsPolygon(null);
        }}
        polygon={meetingPointsPolygon}
        global={global}
        setMeetingPointsLoading={setMeetingPointsLoading}
        setIsMeetingPointsModalOpen={setIsMeetingPointsModalOpen}
        setMeetingPointsPolygon={setMeetingPointsPolygon}
        loading={meetingPointsLoading}
        onLayerCreated={onMeetingPointsLayerCreated}
      />
    </FeatureGate>
    <FeatureGate featureName="transit_scan">
      <TransitScanModal
        open={transitScanState.isModalOpen}
        onClose={() => transitScanActions.setIsModalOpen(false)}
        global={global}
        state={transitScanState}
        actions={transitScanActions}
      />
    </FeatureGate>
    <FleetVehicleModal
      open={vehicleModalOpen}
      onClose={() => { setVehicleModalOpen(false); setEditingVehicle(null); setVehicleDraft(null); }}
      onSave={handleAddOrEditVehicle}
      vehicle={vehicleDraft || editingVehicle}
      points={allPoints}
    />
    <FeatureGate featureName="simulations">
      <SimulationModal
        open={isSimulationModalOpen}
        onClose={() => {
          setIsSimulationModalOpen(false);
          setInitialSelectedLayer(undefined);
          setInitialSelectedFleetId(undefined);
        }}
        fleets={fleets}
        modalLayers={layersForModal}
        onRunSimulation={onRunSimulation}
        setSimulationProgress={setSimulationProgress}
        onOpenDrawer={onOpenSimulationDrawer}
        onEditFleet={onEditFleet}
      />
    </FeatureGate>
    <FeatureGate featureName="generate_rides">
      <GenerateRidesDrawer
        open={isGenerateRidesDrawerOpen}
        onClose={() => setIsGenerateRidesDrawerOpen(false)}
        modalLayers={layersForModal}
        onGenerateRides={onGenerateRides}
      />
    </FeatureGate>
    <FeatureGate featureName="fixed_routes">
      <FixedRoutesDrawer
        open={isFixedRoutesDrawerOpen}
        onClose={() => setIsFixedRoutesDrawerOpen(false)}
        selectedRouteId={selectedRouteId}
      />
    </FeatureGate>
    <FeatureGate featureName="attribute_table">
      <AttributeTableDrawer
        open={isAttributeTableDrawerOpen}
        onClose={() => setIsAttributeTableDrawerOpen(false)}
        layers={mapLayers}
        selectedLayerId={attributeTableLayerId}
        onLayerChange={setAttributeTableLayerId}
        onUpdateShape={onAttributeTableShapeUpdate}
        onUndo={onAttributeTableUndo}
        onRedo={onAttributeTableRedo}
        canUndo={attributeTableCanUndo}
        canRedo={attributeTableCanRedo}
        editable={attributeTableEditable}
      />
    </FeatureGate>
  </>;
};

export default MapModalsManager; 
